import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertCircle, Info } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, Callout, Field, SidePanel, inputCls, textareaCls } from '../../components/ui';
import {
  type DirectoryCase,
  type Envelope,
  type Page,
  type Preview,
  command,
  directoryRequest,
} from '../../lib/personnel-api';
import {
  ErrorNotice,
  Loading,
  PanelSection,
  PreviewCommit,
  StateBadge,
  caseReason,
  useDirectoryCommand,
} from './parts';

type CaseDetail = DirectoryCase & {
  kind: string;
  related?: {
    referenceIds?: string[];
    referenceId?: string;
    observationSourceKey?: string;
    uploadId?: number;
    rowCount?: number;
    personId?: string;
    brigadeId?: string;
    jobId?: string;
  };
  assigneeOptions?: { userId: number; displayName: string }[];
  sourceProgress?: {
    state: 'pending' | 'running' | 'completed' | 'failed';
    processedCount: number;
    rowCount: number;
  };
  mappingOptions?: { sourceInstanceId: string; label: string; siteId: string; evidenceIds: string[] }[];
};
export function CasePanel({
  id,
  caps,
  onClose,
  onChanged,
}: { id: string; caps: string[]; onClose: () => void; onChanged: () => void }) {
  const stableCommand = useDirectoryCommand();
  const queryClient = useQueryClient();
  const publishedResolution = useRef('');
  const [submitted, setSubmitted] = useState(false);
  const [submittedRows, setSubmittedRows] = useState(0);
  const item = useQuery({
    queryKey: ['personnel', 'case', id],
    queryFn: () => directoryRequest<CaseDetail>(`/api/personnel/cases/${encodeURIComponent(id)}`),
    refetchInterval: (query) => {
      const current = query.state.data?.data;
      if (
        current?.state === 'resolved' ||
        current?.sourceProgress?.state === 'completed' ||
        current?.sourceProgress?.state === 'failed'
      )
        return false;
      return submitted ||
        current?.sourceProgress?.state === 'pending' ||
        current?.sourceProgress?.state === 'running'
        ? 3000
        : false;
    },
  });
  const [assignee, setAssignee] = useState('');
  const [basis, setBasis] = useState('');
  const [source, setSource] = useState('');
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState<Envelope<Preview> | null>(null);
  const [mapping, setMapping] = useState<unknown>(null);
  const c = item.data?.data;
  useEffect(() => {
    if (!c || c.state !== 'resolved') return;
    const resolution = `${c.id}:${c.revision}`;
    if (publishedResolution.current === resolution) return;
    publishedResolution.current = resolution;
    queryClient.setQueriesData<Envelope<Page<DirectoryCase>>>({ queryKey: ['personnel', 'cases'] }, (old) =>
      old
        ? {
            ...old,
            data: {
              ...old.data,
              items: old.data.items.map((row) =>
                row.id === c.id
                  ? {
                      ...row,
                      state: c.state,
                      revision: c.revision,
                      reasonCode: c.reasonCode,
                      assigneeUserId: c.assigneeUserId,
                      allowedActions: c.allowedActions,
                      allowedActionRefs: c.allowedActionRefs,
                    }
                  : row,
              ),
            },
          }
        : old,
    );
    void queryClient.invalidateQueries({ queryKey: ['personnel', 'cases'] });
  }, [c, queryClient]);
  const sourceProgress =
    c?.sourceProgress ??
    (submitted ? { state: 'pending' as const, processedCount: 0, rowCount: submittedRows } : undefined);
  const update = () => {
    void item.refetch();
    onChanged();
  };
  const assign = async () => {
    if (!c) return;
    setBusy(true);
    try {
      await directoryRequest(
        `/api/personnel/cases/${encodeURIComponent(id)}`,
        'PATCH',
        stableCommand({ assigneeUserId: assignee ? Number(assignee) : null, basis: basis.trim() }, [
          { kind: 'case', id, revision: c.revision },
        ]),
      );
      update();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  };
  const mappingPreview = async () => {
    if (!c) return;
    const option = c.mappingOptions?.find((o) => o.sourceInstanceId === source);
    if (!option) return;
    const input = {
      caseId: id,
      ...(c.related?.uploadId
        ? { uploadId: c.related.uploadId }
        : {
            referenceIds: c.related?.referenceIds ?? (c.related?.referenceId ? [c.related.referenceId] : []),
            observationSourceKeys: c.related?.observationSourceKey ? [c.related.observationSourceKey] : [],
          }),
      targetSourceInstanceId: source,
      evidenceIds: option.evidenceIds,
      basis: basis.trim(),
    };
    try {
      setPreview(await directoryRequest<Preview>('/api/personnel/source-mappings/preview', 'POST', input));
      setMapping(input);
    } catch (e) {
      setError(e);
    }
  };
  return (
    <>
      <SidePanel open label="Случай справочника" title="Разбор случая" onClose={onClose}>
        <div className="space-y-5">
          <ErrorNotice error={item.error} retry={() => void item.refetch()} />
          {item.isLoading && <Loading />}
          {c && (
            <>
              <StateBadge state={c.state} />
              <PanelSection title="Причина">
                <p className="text-sm font-medium">{caseReason(c.reasonCode, c.message)}</p>
                <p className="break-all text-xs text-ink-3">
                  Случай {c.id} · редакция {c.revision}
                </p>
              </PanelSection>
              <Callout icon={Info}>
                Случай закрывается после устранения причины предметной командой. Назначение ответственного не
                меняет данные и не закрывает случай.
              </Callout>
              {c.allowedActions?.includes('cases.manage') && (
                <PanelSection title="Ответственный">
                  <p className="text-sm text-ink-2">
                    Сейчас:{' '}
                    {c.assigneeOptions?.find((u) => String(u.userId) === String(c.assigneeUserId))
                      ?.displayName ?? (c.assigneeUserId ? 'Назначен' : 'Не назначен')}
                  </p>
                  <Field label="Назначить ответственного">
                    <select
                      aria-label="Назначить ответственного"
                      className={inputCls}
                      value={assignee}
                      onChange={(e) => setAssignee(e.target.value)}
                    >
                      <option value="">Без ответственного</option>
                      {c.assigneeOptions?.map((u) => (
                        <option key={u.userId} value={u.userId}>
                          {u.displayName}
                        </option>
                      ))}
                    </select>
                  </Field>
                  <Field label="Основание изменения">
                    <textarea
                      aria-label="Основание изменения"
                      className={textareaCls}
                      value={basis}
                      onChange={(e) => setBasis(e.target.value)}
                    />
                  </Field>
                  <Button disabled={busy || !basis.trim()} onClick={() => void assign()}>
                    Сохранить ответственного
                  </Button>
                </PanelSection>
              )}
              <PanelSection title="Разрешённый путь решения">
                {c.related?.uploadId && (
                  <Callout icon={Info}>
                    Подтверждение источника всей выгрузки
                    {c.related.rowCount == null ? '' : ` · ${c.related.rowCount} строк`}. Люди из разных
                    регистраций сами не объединяются. После сохранения наблюдения будут обработаны с явно
                    подтверждённым происхождением.
                  </Callout>
                )}
                {sourceProgress && (
                  <Callout
                    icon={Info}
                    tone={
                      sourceProgress.state === 'failed'
                        ? 'warn'
                        : sourceProgress.state === 'completed'
                          ? 'ok'
                          : 'neutral'
                    }
                  >
                    <p role="status" aria-live="polite" className="font-medium">
                      {sourceProgress.state === 'completed'
                        ? 'Обработка выгрузки завершена'
                        : sourceProgress.state === 'failed'
                          ? 'Обработка выгрузки завершилась ошибкой'
                          : 'Источник подтверждён, выгрузка обрабатывается'}
                    </p>
                    <p className="mt-1">
                      Обработано {sourceProgress.processedCount} из {sourceProgress.rowCount} строк. Повторное
                      подтверждение источника не требуется.
                    </p>
                    {sourceProgress.state === 'failed' && (
                      <p className="mt-1">Продолжение выполняется повтором сохранённого задания обработки.</p>
                    )}
                  </Callout>
                )}
                {c.kind === 'source' ? (
                  c.state === 'resolved' || sourceProgress ? null : caps.includes('source_mapping.manage') &&
                    c.mappingOptions?.length ? (
                    <>
                      <Field label="Доказанный источник">
                        <select
                          aria-label="Доказанный источник"
                          className={inputCls}
                          value={source}
                          onChange={(e) => setSource(e.target.value)}
                        >
                          <option value="">Выберите источник</option>
                          {c.mappingOptions.map((s) => (
                            <option key={s.sourceInstanceId} value={s.sourceInstanceId}>
                              {s.label} · {s.siteId}
                            </option>
                          ))}
                        </select>
                      </Field>
                      <Field label="Основание сопоставления источника">
                        <textarea
                          aria-label="Основание сопоставления источника"
                          className={textareaCls}
                          value={basis}
                          onChange={(e) => setBasis(e.target.value)}
                        />
                      </Field>
                      <Button disabled={!source || !basis.trim()} onClick={() => void mappingPreview()}>
                        Проверить сопоставление источника
                      </Button>
                    </>
                  ) : (
                    <Callout icon={AlertCircle} tone="warn">
                      Происхождение ещё не доказано. Нужны сведения об источнике и право разрешения этой
                      ситуации; произвольная подмена ключа недоступна.
                    </Callout>
                  )
                ) : c.related?.personId &&
                  c.allowedActionRefs?.some(
                    (ref) => ref.kind === 'identity_review' || ref.kind === 'account_binding',
                  ) ? (
                  <Link
                    className="inline-flex min-h-10 items-center text-sm font-medium text-accent"
                    to={`/personnel?person=${encodeURIComponent(c.related.personId)}`}
                  >
                    Открыть карточку и явный разбор
                  </Link>
                ) : c.related?.brigadeId && c.allowedActionRefs?.some((ref) => ref.kind === 'membership') ? (
                  <Link
                    className="inline-flex min-h-10 items-center text-sm font-medium text-accent"
                    to={`/brigades?brigade=${encodeURIComponent(c.related.brigadeId)}`}
                  >
                    Открыть состав бригады
                  </Link>
                ) : (
                  <p className="text-sm text-ink-2">
                    {c.allowedActionRefs?.length
                      ? 'Откройте связанные карточки и примените предметную команду с проверкой последствий.'
                      : 'Нет доступных действий для текущих полномочий.'}
                  </p>
                )}
              </PanelSection>
              {c.related?.jobId && (
                <JobState id={c.related.jobId} canRetry={caps.includes('jobs.retry')} onChanged={update} />
              )}
              <ErrorNotice error={error} />
            </>
          )}
        </div>
      </SidePanel>
      {preview && (
        <PreviewCommit
          title="Сопоставить источник?"
          preview={preview}
          summary={
            <div className="space-y-2">
              <p className="font-semibold">
                {c?.related?.uploadId
                  ? `Подтвердить происхождение всей выгрузки № ${c.related.uploadId}`
                  : 'Сопоставить неизвестные ссылки с доказанным источником'}
              </p>
              <p>
                Источник:{' '}
                <strong>
                  {c?.mappingOptions?.find((option) => option.sourceInstanceId === source)?.label ??
                    'Выбранный доказанный экземпляр'}
                </strong>
              </p>
              {(preview.data.rows ?? c?.related?.rowCount) != null && (
                <p>Наблюдений для обработки: {preview.data.rows ?? c?.related?.rowCount}.</p>
              )}
              <p>Люди не объединяются; алиасы этим действием не подтверждаются.</p>
              <p>Основание: {basis.trim()}</p>
            </div>
          }
          input={mapping}
          endpoint="/api/personnel/source-mappings"
          onCancel={() => setPreview(null)}
          onDone={() => {
            if (c?.related?.uploadId) {
              setSubmitted(true);
              setSubmittedRows(preview.data.rows ?? c.related.rowCount ?? 0);
            }
            setPreview(null);
            update();
          }}
        />
      )}
    </>
  );
}
export function JobState({
  id,
  canRetry,
  onChanged,
}: { id: string; canRetry: boolean; onChanged?: () => void }) {
  const job = useQuery({
    queryKey: ['personnel', 'job', id],
    queryFn: () =>
      directoryRequest<{ id: string; state: string; revision: number; reasonCode?: string }>(
        `/api/personnel/jobs/${encodeURIComponent(id)}`,
      ),
    refetchInterval: 5000,
  });
  const [basis, setBasis] = useState('');
  const [error, setError] = useState<unknown>(null);
  return (
    <PanelSection title="Обработка справочника">
      <p role="status" className="text-sm">
        {job.data?.data.state === 'done'
          ? 'Завершена'
          : job.data?.data.state === 'failed'
            ? 'Ошибка обработки'
            : 'Обработка ожидается или выполняется'}
      </p>
      {job.data?.data.reasonCode && (
        <p className="text-xs text-ink-3">{caseReason(job.data.data.reasonCode)}</p>
      )}
      {canRetry && job.data?.data.state === 'failed' && (
        <>
          <Field label="Основание повтора">
            <input
              aria-label="Основание повтора"
              className={inputCls}
              value={basis}
              onChange={(e) => setBasis(e.target.value)}
            />
          </Field>
          <Button
            disabled={!basis.trim()}
            onClick={async () => {
              if (!job.data) return;
              try {
                await directoryRequest(
                  `/api/personnel/jobs/${encodeURIComponent(id)}/retry`,
                  'POST',
                  command({ basis: basis.trim() }, [{ kind: 'job', id, revision: job.data.data.revision }]),
                );
                void job.refetch();
                onChanged?.();
              } catch (e) {
                setError(e);
              }
            }}
          >
            Повторить обработку
          </Button>
        </>
      )}
      <ErrorNotice error={job.error ?? error} />
    </PanelSection>
  );
}
